import React from 'react'
import { useSelector } from 'react-redux'
import { makeStyles, Paper, Popper } from '@material-ui/core'
import CheckedIcon from '@material-ui/icons/CheckCircle'
import CircleIcon from '@material-ui/icons/RadioButtonUnchecked'
import FilledCircleIcon from '@material-ui/icons/FiberManualRecord'
import { TreeView, TreeItem } from '@material-ui/lab'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import clsx from 'clsx'
import moment from 'moment'

import store from '../../../../redux/store'
import Formatter from '../../../../utils/Formatter'
import { getStatusAsClassName } from '../QuickLook'
import RecordActions from '../recordActions'

export function renderCellExpand(params) {
  return (
    <GridCellExpand
      value={params.value ? params.value.toString() : ''}
      width={params.colDef.width}
    />
  )
}

const BoolElement = (props) => {
  const { isEnabled } = props
  return (
    <div style={{ margin: 'auto' }}>
      {isEnabled ? <CheckedIcon /> : <CircleIcon />}
    </div>
  )
}

export function renderCellAiCaptions(params) {
  const isEnabled = Boolean(params.value.isEnabled)
  return <BoolElement isEnabled={isEnabled} />
}

export function renderCellBoolean(params) {
  const isTrue = Boolean(params.row[params.field])
  return <BoolElement isEnabled={isTrue} />
}

export function renderCellList(params) {
  const { value } = params
  if (!value) {
    return null
  }
  if (typeof value === 'string') {
    return <EllipsedText text={value} />
  }
  return (
    <div
      style={{
        overflow: 'auto',
        overflowX: 'hidden',
        width: '100%',
        height: '100%',
      }}
    >
      <TreeView
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
      >
        {value.map((item, i) => (
          <TreeItem nodeId={i + item} label={item} />
        ))}
      </TreeView>
    </div>
  )
}

export function renderCellStatus(params) {
  const { value } = params
  return <div >
    <FilledCircleIcon
        className={'event-status ' + getStatusAsClassName(value)}
      />{value}
  </div>
}

export function renderCellTreeView(params) {
  // loop through keys of an object and create nodes recursively where the value is another object
  const getTreeItems = (obj, id = 0) => {
    return Object.keys(obj).map((key, i) => {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        // branch
        return (
          <TreeItem nodeId={i + id} label={key} key={i + id + key}>
            {getTreeItems(obj[key], i + id + Object.keys(obj[key]).length)}
          </TreeItem>
        )
      } else {
        // leaf
        return <TreeItem label={key} nodeId={i + id} key={i + id + key}/>
      }
    })
  }

  const json = params.row[params.field]
  if (!json) {
    return null
  } else {
    return (
      <div
        key={`tree view container ${params.row.id}`}
        style={{
          overflow: 'auto',
          overflowX: 'hidden',
          width: '100%',
          height: '100%',
        }}
      >
        <TreeView
          key={`tree view ${params.row.id}`}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
        >
          {getTreeItems(json)}
        </TreeView>
      </div>
    )
  }
}

export function renderCellEncoder(params) {
  const encoderDetails = params.row.encoderDetails
  let isOnline, name
  if (!encoderDetails) {
    isOnline = false
    name = 'Not Assigned'
  } else {
    isOnline = encoderDetails.online
    name = encoderDetails.simpleName
  }
  return (
    <div style={{ display: 'inline-flex', overflow: 'hidden' }}>
      <FilledCircleIcon
        style={{ margin: 'auto', color: isOnline ? 'green' : 'gray' }}
      />
      <span className='ellipsed-text'>{name}</span>
    </div>
  )
}

const EllipsedText = (props) => {
  const { text } = props
  return <span className='ellipsed-text'>{text}</span>
}

export function renderCellPlayerActions(params) {
  const state = store.getState()
  const eventID = params.id
  const event = state.data.events[eventID]
  const encoders = state.data.encoders
  const handleRecord = () => params.row.eventActions.record()
  const handleStop = () => params.row.eventActions.stop()
  const handlePause = () => params.row.eventActions.pause()
  return (
    <div className='action-buttons'>
      <RecordActions
        event={event}
        encoders={{ data: encoders }}
        handleRecord={(e) => {
          e.stopPropagation()
          handleRecord(event)
        }}
        handleStop={(e) => {
          e.stopPropagation()
          handleStop(event)
        }}
        handlePause={(e) => {
          e.stopPropagation()
          handlePause(event)
        }}
      />
    </div>
  )
}

export function renderCellDate(params) {
  const timeZone = store.getState().data.client.timeZone
  const date = params.value
  const clientTZabbr = moment.tz(timeZone).format('z')
  return (
    <EllipsedText
      text={Formatter.getHeadingDateFormat(date) + ' ' + clientTZabbr}
    />
  )
}

const useStyles = makeStyles(() => ({
  smallP: {
    '& p': {
      margin: 0,
      lineHeight: '1.5em',
      whiteSpace: 'break-spaces'
    }
  },
  descriptionContainer: {
    width: '50vw',
    maxWidth: '60rem',
    maxHeight: '30vh',
    overflow: 'auto',
    boxShadow: '2px 1px 11px -1px',
    padding: '1rem',
  }
}))

const GridCellExpand = function GridCellExpand(props) {
  const { width, value } = props
  const wrapper = React.useRef(null)
  const cellDiv = React.useRef(null)
  const cellValue = React.useRef(null)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [showFullCell, setShowFullCell] = React.useState(false)
  const [showPopper, setShowPopper] = React.useState(false)
  const classes = useStyles()

  const handleMouseEnter = () => {
    setShowPopper(true)
    setAnchorEl(wrapper.current.parentElement)
    setShowFullCell(true)
  }

  const handleMouseLeave = () => {
    setShowFullCell(false)
  }

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined
    }

    function handleKeyDown(nativeEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [setShowFullCell, showFullCell])
  return (
    <div
      style={{ width: '100%' }}
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classes.smallP}
    >
      <div
        ref={cellDiv}
        style={{
          height: 1,
          width,
          display: 'block',
          position: 'absolute',
        }}
      />
      <div
        ref={cellValue}
        className={clsx('cellValue', 'ellipsed-text')}
        dangerouslySetInnerHTML={{ __html: value }}
      />
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{ zIndex: 10000, marginTop: '-70px' /*row height */ }}
        >
          <Paper
            style={{ minWidth: width }}
            elevation={1}
            className={classes.descriptionContainer}
          >
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </Paper>
        </Popper>
      )}
    </div>
  )
}
